import React, { useState } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";

/* ─── Add User Modal ──────────────────────────────────────────────────
   Shared popup used by: Admin → Users → "New User", and
   Admin → Direct Orders → Customer → "Create new user".
   onCreated(user) receives the freshly created user record.
   ──────────────────────────────────────────────────────────────────── */
const AddUserModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "user",
    accountType: "individual",
    address: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Name, email and password are required.");
      return;
    }
    try {
      setSaving(true);
      const { data } = await API.post("/admin/users", form);
      toast.success("User created!");
      onCreated(data.user);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          borderRadius: "var(--radius-lg)",
          padding: 32,
          maxWidth: 480,
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h3>➕ Add New User</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              fontSize: "1.2rem",
              cursor: "pointer",
              color: "var(--text-muted)",
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Full Name *
            </label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Full name"
              required
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Email *
            </label>
            <input
              className="form-control"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="name@example.com"
              required
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Phone
            </label>
            <input
              className="form-control"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="10-digit mobile"
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Password *
            </label>
            <input
              className="form-control"
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="Set a password for this user"
              required
            />
          </div>
          <div>
            <label
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Address
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label
                style={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Role
              </label>
              <select
                className="form-control"
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Account Type
              </label>
              <select
                className="form-control"
                value={form.accountType}
                onChange={(e) => set("accountType", e.target.value)}
              >
                <option value="individual">Individual</option>
                <option value="business">Business</option>
              </select>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary"
            style={{ flex: 1, justifyContent: "center" }}
          >
            {saving ? "Creating…" : "Create User"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
            style={{ flex: 1, justifyContent: "center" }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddUserModal;
